import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, BookOpen } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toTenantInput, fromTenantInput, fmtTenant } from '../components/crew/tenantTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const T = [['block_off', 'Block-out'], ['takeoff_time', 'Despegue'], ['landing_time', 'Aterrizaje'], ['block_on', 'Block-in']];
const hm = (m) => `${Math.floor((m || 0) / 60)}:${String((m || 0) % 60).padStart(2, '0')}`;
const errText = (e) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : d?.message || 'Error'; };

function EntryDialog({ open, entry, flights, tz, onClose, onSaved }) {
    const [f, setF] = useState({});
    const [reason, setReason] = useState('');
    useEffect(() => { setF(entry ? Object.fromEntries(T.map(([k]) => [k, toTenantInput(entry[k], tz)])) : {}); setReason(''); }, [entry, open, tz]);
    const payloadTimes = () => Object.fromEntries(T.filter(([k]) => f[k]).map(([k]) => [k, fromTenantInput(f[k], tz)]));
    const save = async () => {
        try {
            if (entry) await axios.patch(`${API}/logbook/${entry.id}/correct`, { changes: { ...payloadTimes(), landings: Number(f.landings || entry.landings || 1) }, reason });
            else await axios.post(`${API}/logbook`, { flight_id: f.flight_id, landings: Number(f.landings || 1), ...payloadTimes() });
            toast.success(entry ? 'Corrección guardada y auditada' : 'Entrada creada');
            onSaved();
        } catch (e) { toast.error(errText(e)); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="logbook-dialog">
                <DialogHeader>
                    <DialogTitle>{entry ? `Corregir ${entry.flight_number || ''}` : 'Nueva entrada'}</DialogTitle>
                    <DialogDescription>{`Horas en zona ${tz}. `}{entry ? 'Las entradas no se borran. La corrección queda en auditoría con el valor anterior, el nuevo y el motivo.' : 'Solo vuelos tuyos con asignación confirmada.'}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {!entry && (
                        <Select value={f.flight_id || ''} onValueChange={v => setF({ ...f, flight_id: v })}>
                            <SelectTrigger data-testid="logbook-flight"><SelectValue placeholder="Vuelo" /></SelectTrigger>
                            <SelectContent>{flights.map(x => <SelectItem key={x.id} value={x.id}>{x.date} {x.flight_number} {x.origin}-{x.destination}</SelectItem>)}</SelectContent>
                        </Select>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                        {T.map(([k, l]) => <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input type="datetime-local" value={f[k] || ''} onChange={e => setF({ ...f, [k]: e.target.value })} data-testid={`logbook-${k}`} /></div>)}
                    </div>
                    {entry && <div className="space-y-1"><Label className="text-xs">Motivo (mínimo 10 caracteres)</Label><Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} data-testid="logbook-reason" /></div>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    <Button onClick={save} disabled={entry ? reason.trim().length < 10 : !f.flight_id} data-testid="logbook-save">Guardar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function MyLogbookPage() {
    const [entries, setEntries] = useState([]);
    const [totals, setTotals] = useState(null);
    const [flights, setFlights] = useState([]);
    const [dlg, setDlg] = useState(null);
    const [tz, setTz] = useState('UTC');
    const load = useCallback(async () => {
        try {
            const [e, t, f] = await Promise.all([axios.get(`${API}/logbook`), axios.get(`${API}/logbook/totals`), axios.get(`${API}/my/flights?days_back=30&days_ahead=0`)]);
            setEntries(e.data); setTotals(t.data); setTz(f.data.timezone || 'UTC');
            const logged = new Set(e.data.map(x => x.flight_id));
            setFlights(f.data.flights.filter(x => !logged.has(x.id)));
        } catch (e) { toast.error(errText(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);
    return (
        <div className="space-y-6" data-testid="my-logbook-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">Mi bitácora</h1>
                    <p className="text-muted-foreground mt-1">Totales calculados desde tus propias entradas</p>
                </div>
                <Button onClick={() => setDlg({ entry: null })} data-testid="logbook-new"><Plus className="w-4 h-4 mr-2" />Nueva entrada</Button>
            </div>
            {totals && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="logbook-totals">
                    {[['Entradas', totals.entries], ['Horas bloque', hm(totals.block_minutes)], ['Horas vuelo', hm(totals.flight_minutes)], ['Aterrizajes', totals.landings]].map(([l, v]) => (
                        <Card key={l} className="card-aviation"><CardContent className="p-3"><p className="text-xl font-bold font-mono">{v}</p><p className="text-[10px] uppercase text-muted-foreground">{l}</p></CardContent></Card>
                    ))}
                </div>
            )}
            {!entries.length ? <p className="text-muted-foreground flex items-center gap-2"><BookOpen className="w-4 h-4" />Sin entradas todavía.</p> : (
                <div className="overflow-x-auto rounded-lg border border-border/40"><table className="w-full text-sm" data-testid="logbook-table">
                    <thead className="bg-muted/30 text-[10px] uppercase text-muted-foreground"><tr>{['Vuelo', 'Posición', 'Ruta', 'Matrícula', 'Block-out', 'Block-in', 'Bloque', 'Vuelo', ''].map((h, i) => <th key={i} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
                    <tbody>{entries.map(e => (
                        <tr key={e.id} className="border-t border-border/20" data-testid={`logbook-row-${e.id}`}>
                            <td className="px-3 py-2 font-mono">{e.flight_number}{e.corrected && <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400">corregida</span>}</td>
                            <td className="px-3 py-2 font-mono text-xs" data-testid={`logbook-pos-${e.id}`}>{e.flight_position === 'FO_ADD' ? 'FO adicional' : (e.flight_position || '—')}{e.training_flight && <span className="ml-1 text-[10px] text-sky-700 dark:text-sky-400">instrucción</span>}</td>
                            <td className="px-3 py-2 font-mono text-xs">{e.origin || e.departure_airport}-{e.destination || e.arrival_airport}</td>
                            <td className="px-3 py-2 font-mono text-xs">{e.aircraft_registration}</td>
                            <td className="px-3 py-2 font-mono text-xs">{fmtTenant(e.block_off, tz)}</td>
                            <td className="px-3 py-2 font-mono text-xs">{fmtTenant(e.block_on, tz)}</td>
                            <td className="px-3 py-2 font-mono">{hm(e.block_time_minutes)}</td>
                            <td className="px-3 py-2 font-mono">{hm(e.flight_time_minutes)}</td>
                            <td className="px-3 py-2"><Button size="icon" variant="ghost" onClick={() => setDlg({ entry: e })} data-testid={`logbook-correct-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button></td>
                        </tr>
                    ))}</tbody>
                </table></div>
            )}
            <EntryDialog open={!!dlg} entry={dlg?.entry} flights={flights} tz={tz} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />
        </div>
    );
}
